import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button, Stack } from '@mui/material'
import { useBookingContext } from '@providers/booking'
import { useLocationContext } from '@providers/location'
import { useSessionContext } from '@providers/session/session_context'
import { STICKY_HEADER_HEIGHT_PX } from '@shared/constants'
import { i18WithDefault as t } from '@shared/locale'
import { getServiceTypeByName, serviceTypeForHomePage } from '@shared/service_types'
import { useRoutes } from 'app/use_routes'
import { Footer } from 'components/footer'
import { Logo } from 'components/logo'
import { SearchBar } from 'components/search_bar'
import { ServiceTypeSelector } from 'components/service_type_selector'
import { useUserStatus } from 'hooks/use_userstatus'
import { useClientBookingSearchConfigs } from 'service-api/src/hooks/use_client_booking_search_configs'
import { useClientLocationBookingConf } from 'service-api/src/hooks/use_client_location_booking_conf'
import { useClientLocationServiceType } from 'service-api/src/hooks/use_client_location_service_type'
import { useClientLocationSpecies } from 'service-api/src/hooks/use_client_location_species'
import { useClientLocationUserProfile } from 'service-api/src/hooks/use_client_location_user_profiles'
import { isHttpForbidden, isHttpUnauthorized, isNotFound } from 'service-api/src/shared/request'
import { injectReachScript } from './helper'
import { findMembershipService } from 'components-ui/src/shared/memberships'
import { Spacer1, Spacer2, Spacer3 } from 'components-ui/src/spacer/spacer'
import { PackageIcon } from 'components-ui/src/asset/package_icon'

export const Booking = () => {
  const { accountName: locationName = '', serviceTypeName = 'boarding' } = useParams()
  const { navigateRoute } = useRoutes()
  const ctx = useBookingContext()
  const sessionCtx = useSessionContext()
  const appIsReady = sessionCtx.state.status === 'READY'
  const userStatus = useUserStatus()
  const { buildPath } = useRoutes()
  const bookingCtx = useBookingContext()
  const { location, locationTheme, isLoading: isLoadingLocation } = useLocationContext()
  const serviceTypes = useClientLocationServiceType(locationName, {
    restrictions: true,
  })
  const configs = useClientBookingSearchConfigs(locationName, serviceTypeName)
  const lupId = sessionCtx.state.user?.lupId || ''

  const st = serviceTypes.data?.results?.find((st) => st.serviceType?.name === serviceTypeName)
  const serviceTypeConfig = useClientLocationBookingConf(locationName, st?.id)
  const species = useClientLocationSpecies(locationName, true)
  const userProfile = useClientLocationUserProfile(locationName, appIsReady ? lupId : '', [
    'locationPetProfiles',
    'locationPetProfiles.images.asset',
    'locationPetProfiles.locationSpecies.species',
    'locationPetProfiles.breed',
    'locationPetProfiles.profileImage.asset',
  ])

  useEffect(() => {
    bookingCtx.dispatch({ type: 'RESET_STATE' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (ctx.state.currentState !== 'search') {
      ctx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'search' } })
    }
  })

  if (userProfile.isError) {
    if (isHttpUnauthorized(userProfile.error) || isHttpForbidden(userProfile.error)) {
      setTimeout(() => {
        ctx.dispatch({ type: 'RESET_STATE_FULL' })
        sessionCtx.dispatch({ type: 'SIGNOUT' })
      }, 0)
      window.location.reload()
    }
  }

  useEffect(() => {
    if (serviceTypeName === 'temper-test') {
      navigateRoute('searchServiceType', { locationName, serviceTypeName: 'boarding' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTypeName])

  useEffect(() => {
    if (configs.isError && isNotFound(configs.error)) {
      if (serviceTypes.isSuccess) {
        const stRedirect = serviceTypeForHomePage(serviceTypes.data.results)
        if (stRedirect) {
          window.location.href = buildPath('searchServiceType', {
            accountName: locationName,
            serviceTypeName: stRedirect?.serviceType?.name || 'boarding',
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs.isError, serviceTypes.isSuccess])

  useEffect(() => {
    if (configs.data && configs.isSuccess) {
      ctx.dispatch({
        type: 'SET_BOOKING_CONFIG',
        payload: {
          value: configs.data,
          serviceTypeName,
          isNewCustomer: !userStatus.isLoggedIn,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs.data, userProfile.data, userStatus.isLoggedIn])

  useEffect(() => {
    if (serviceTypeConfig.data) {
      ctx.dispatch({
        type: 'SET_SERVICE_TYPE_CONFIG',
        payload: { value: serviceTypeConfig.data?.results },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs.data, serviceTypeConfig.data?.results])

  useEffect(() => {
    if (species.data?.results?.length) {
      ctx.dispatch({
        type: 'SET_SPECIES_CONFIG',
        payload: { value: species.data?.results },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [species.data])

  useEffect(() => {
    if (species.isSuccess && configs.isSuccess) {
      // this has to take place after booking config to render only correct species for the services offered
      if (userProfile.data) {
        ctx.dispatch({
          type: 'SET_PET_PROFILES',
          payload: { value: userProfile.data.locationPetProfiles || [] },
        })

        ctx.dispatch({
          type: 'SET_PAYMENT_CONTACT',
          payload: {
            value: userProfile.data,
          },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [species.isSuccess, configs.isSuccess, userProfile.data])

  useEffect(() => {
    if (location) {
      ctx.dispatch({
        type: 'SET_LOCATION_CONFIG',
        payload: { value: location },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    if (serviceTypes.data) {
      const temperTest = serviceTypes.data.results.find((s) => s.serviceType!.name === 'temper-test')
      ctx.dispatch({ type: 'SET_TEMPER_TEST_DATA', payload: { value: temperTest } })
      const membershipService = findMembershipService(serviceTypes.data.results)
      ctx.dispatch({ type: 'SET_MEMBERSHIP_SERVICE_DATA', payload: { value: membershipService } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTypes.data, st?.id])

  useEffect(() => {
    if (st) {
      ctx.dispatch({ type: 'SET_SERVICE_TYPE', payload: { value: st } })
      ctx.dispatch({ type: 'SET_SERVICE_TYPES', payload: { value: serviceTypes.data!.results } })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.id])

  useEffect(() => {
    document.title = t('title.startBooking')
  }, [])

  useEffect(() => {
    // this is toggled in retool by "Marketing Enabled?" flag on subscriber group
    if (location?.subscriberGroup?.everflowEnabled) {
      injectReachScript(location)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.subscriberGroup?.everflowEnabled, locationName])

  const navigateToPackages = useCallback(() => {
    ctx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'searchResults' } })
    navigateRoute('searchOffersResults', { serviceTypeName: 'packages' })
  }, [ctx, navigateRoute])

  const navigateToBooking = useCallback(() => {
    navigateRoute('profileBookings', {})
  }, [navigateRoute])

  return (
    <Box
      sx={{
        height: `calc(100vh - ${STICKY_HEADER_HEIGHT_PX}px)`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div>
        <Spacer1 />
        <Logo isLoading={isLoadingLocation} asset={locationTheme?.logo?.[0]?.asset} location={location} />
        <Spacer2 />
        <ServiceTypeSelector
          isLoading={serviceTypes.isLoading}
          services={serviceTypes.data?.results || []}
          onSelect={lupId ? () => userProfile.refetch() : undefined}
        />
        <Spacer3 />
        <SearchBar isLoading={userStatus.isLoggedIn ? !userProfile.isSuccess : false} />

        <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
          <Spacer3 />
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="left" alignItems="left" spacing={1}>
            {userProfile.isSuccess && (
              <Button variant="outlined" color="primary" size="large" onClick={navigateToBooking}>
                {t('global.label.viewExistingBookings')}
              </Button>
            )}
            {getServiceTypeByName('packages', serviceTypes.data?.results)?.canBeStandalone && (
              <Button
                variant="text"
                color="primary"
                size="large"
                startIcon={<PackageIcon />}
                onClick={navigateToPackages}
              >
                {t('global.label.purchaseAPackage')}
              </Button>
            )}
          </Stack>
        </Box>
      </div>
      <div>
        <Footer />
        <Spacer3 />
      </div>
    </Box>
  )
}
